import Alpine from 'alpinejs';

window.Alpine = Alpine;

type CookieConsentInstance = {
  init: (config: unknown) => void;
};

const detectCookieConsentLanguage = (): 'sv' | 'en' => {
  const htmlLang = document.documentElement.getAttribute('lang')?.toLowerCase() ?? '';
  return htmlLang.startsWith('en') ? 'en' : 'sv';
};

const cookieConsentConfig = {
  mode: 'bar',
  showRejectAllButton: true,
  customCSS: `
    @media (max-width: 767px) {
      #cconsent-bar {
        padding: 1rem;
      }

      #cconsent-bar .ccb__wrapper {
        flex-direction: column;
        align-items: stretch;
        gap: 0.75rem;
      }

      #cconsent-bar .ccb__left {
        margin: 0;
      }

      #cconsent-bar .ccb__right {
        width: 100%;
        white-space: normal;
      }

      #cconsent-bar .ccb__right > div {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 0.75rem;
        width: 100%;
      }

      #cconsent-bar button,
      #cconsent-bar button.consent-give,
      #cconsent-bar button.consent-decline,
      #cconsent-bar button.ccb__edit {
        width: 100%;
        max-width: 100%;
        margin: 0;
        text-align: center;
      }
    }
  `,
  language: {
    current: 'sv',
    locale: {
      sv: {
        cookieBarLabel: 'Cookiehantering',
        barMainText: 'Vi använder cookies för att förbättra din upplevelse på webbplatsen.',
        closeAriaLabel: 'stäng',
        barLinkSetting: 'Cookieinställningar',
        barBtnAcceptAll: 'Acceptera alla cookies',
        barBtnRejectAll: 'Avvisa alla cookies',
        modalMainTitle: 'Cookieinställningar',
        modalMainText:
          'Cookies är små textfiler som sparas på din enhet när du besöker en webbplats. Vi använder cookies för att anpassa innehåll, analysera trafik och förbättra våra tjänster.',
        modalBtnSave: 'Spara nuvarande inställningar',
        modalBtnAcceptAll: 'Acceptera alla cookies och stäng',
        modalBtnRejectAll: 'Avvisa alla cookies och stäng',
        initModalBtnAgree: 'Jag samtycker',
        initModalBtnDisagree: 'Jag samtycker inte',
        initModalBtnOptions: 'Hantera alternativ',
        modalAffectedSolutions: 'Påverkar följande tjänster:',
        learnMore: 'Läs mer',
        on: 'På',
        off: 'Av',
        enabled: 'är aktiverad.',
        disabled: 'är inaktiverad.',
        checked: 'markerad',
        unchecked: 'omarkerad',
      },
      en: {
        cookieBarLabel: 'Cookie management',
        barMainText: 'We use cookies to improve your experience on the website.',
        closeAriaLabel: 'close',
        barLinkSetting: 'Cookie settings',
        barBtnAcceptAll: 'Accept all cookies',
        barBtnRejectAll: 'Reject all cookies',
        modalMainTitle: 'Cookie settings',
        modalMainText:
          'Cookies are small text files stored on your device when you visit a website. We use cookies to personalize content, analyze traffic, and improve our services.',
        modalBtnSave: 'Save current settings',
        modalBtnAcceptAll: 'Accept all cookies and close',
        modalBtnRejectAll: 'Reject all cookies and close',
        initModalBtnAgree: 'I agree',
        initModalBtnDisagree: 'I do not agree',
        initModalBtnOptions: 'Manage options',
        modalAffectedSolutions: 'Affects the following services:',
        learnMore: 'Learn more',
        on: 'On',
        off: 'Off',
        enabled: 'is enabled.',
        disabled: 'is disabled.',
        checked: 'checked',
        unchecked: 'unchecked',
      },
    },
  },
  categories: {
    necessary: {
      needed: true,
      wanted: true,
      checked: true,
      language: {
        locale: {
          sv: {
            name: 'Nödvändiga cookies',
            description:
              'Dessa cookies är nödvändiga för att webbplatsen ska fungera korrekt och kan inte stängas av i våra system.',
          },
          en: {
            name: 'Necessary cookies',
            description:
              'These cookies are required for the website to function properly and cannot be switched off in our systems.',
          },
        },
      },
    },
    analytics: {
      needed: false,
      wanted: false,
      checked: false,
      language: {
        locale: {
          sv: {
            name: 'Analyscookies',
            description:
              'Analyscookies hjälper oss att förstå hur webbplatsen används så att vi kan förbättra innehåll och funktioner.',
          },
          en: {
            name: 'Analytics cookies',
            description:
              'Analytics cookies help us understand how the website is used so we can improve content and functionality.',
          },
        },
      },
    },
  },
};

const initializeCookieConsent = () => {
  const cookieConsent = (window as Window & { CookieConsent?: CookieConsentInstance }).CookieConsent;

  if (!cookieConsent || typeof cookieConsent.init !== 'function') {
    return;
  }

  cookieConsentConfig.language.current = detectCookieConsentLanguage();

  try {
    cookieConsent.init(cookieConsentConfig);
  } catch {
    // Fail silently if CookieConsent.init throws for any reason.
  }
};

export default {
  init() {
    // JavaScript to be fired on all pages

    initializeCookieConsent();

    Alpine.start();

    // Add hover behavior to navigation popovers
    this.initNavPopovers();

    if (!document.body.classList.contains('home')) {
      return;
    }

    const headerBackground = document.getElementById('site-header-bg') as HTMLElement | null;
    if (!headerBackground) {
      return;
    }

    headerBackground.classList.add('transition', 'duration-300', 'ease-in-out');

    const handleScroll = () => {
      const scrolled = window.scrollY > 0;
      headerBackground.classList.toggle('bg-white', scrolled);
      headerBackground.classList.toggle('bg-transparent', !scrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    handleScroll();
  },
  initNavPopovers() {
    const setupPopovers = () => {
      const menuItems = document.querySelectorAll('.group\\/popover');

      menuItems.forEach((item) => {
        const trigger = item.querySelector('a[popovertarget]') as HTMLAnchorElement | null;
        if (!trigger) return;

        const popoverId = trigger.getAttribute('popovertarget');
        if (!popoverId) return;

        const popover = document.getElementById(popoverId) as HTMLElement & {
          showPopover: () => void;
          hidePopover: () => void;
        };
        if (!popover || typeof popover.showPopover !== 'function') return;

        let hideTimeout: number;

        const handleShowPopover = () => {
          clearTimeout(hideTimeout);
          popover.showPopover();
        };

        const handleHidePopover = () => {
          hideTimeout = window.setTimeout(() => {
            popover.hidePopover();
          }, 150);
        };

        trigger.addEventListener('click', (event) => {
          event.stopPropagation();
        });

        item.addEventListener('mouseenter', handleShowPopover);
        item.addEventListener('mouseleave', handleHidePopover);

        popover.addEventListener('mouseenter', handleShowPopover);
        popover.addEventListener('mouseleave', handleHidePopover);
      });
    };

    if (customElements.get('el-popover')) {
      setupPopovers();
    } else {
      window.addEventListener('elements:ready', setupPopovers);
    }
  },
  finalize() {
    // JavaScript to be fired on all pages, after page specific JS is fired
  },
};
